import type {
  AppEvent, PurchaseCorrectedPayload, PurchaseDeletedPayload, PurchaseLoggedPayload,
} from '../core/types.js';
import { uuid } from '../core/ids.js';
import { localDate, nowIso, tzOffsetMinutes } from '../core/time.js';
import { logEvent } from '../storage/db.js';

export interface LogPurchaseInput {
  habitId: string;
  templateId?: string | undefined;
  methodId?: string | undefined;
  label: string;
  currency: string;
  amount: number;
  quantityPackages?: number | undefined;
  packageQuantity?: number | undefined;
  packageUnitLabel?: string | undefined;
  source: PurchaseLoggedPayload['source'];
  occurredAt?: string | undefined;
}

/**
 * Append a purchase_logged event. Returns the new purchaseId so callers can
 * later correct or delete it. The purchaseId is stable across resolutions.
 */
export async function logPurchase(input: LogPurchaseInput): Promise<{ purchaseId: string; event: AppEvent }> {
  const purchaseId = uuid();
  const occurredAt = input.occurredAt ?? nowIso();
  const payload: PurchaseLoggedPayload = {
    purchaseId,
    label: input.label,
    currency: input.currency,
    amount: input.amount,
    quantityPackages: input.quantityPackages ?? 1,
    source: input.source,
  };
  if (input.templateId !== undefined) payload.templateId = input.templateId;
  if (input.methodId !== undefined) payload.methodId = input.methodId;
  if (input.packageQuantity !== undefined) payload.packageQuantity = input.packageQuantity;
  if (input.packageUnitLabel !== undefined) payload.packageUnitLabel = input.packageUnitLabel;

  const event = await logEvent({
    type: 'purchase_logged',
    habitId: input.habitId,
    occurredAt,
    localDate: localDate(new Date(occurredAt)),
    timezoneOffsetMinutes: tzOffsetMinutes(new Date(occurredAt)),
    payload: { ...payload },
  });
  return { purchaseId, event };
}

export async function correctPurchase(
  habitId: string,
  payload: PurchaseCorrectedPayload,
): Promise<AppEvent> {
  const occurredAt = nowIso();
  return logEvent({
    type: 'purchase_corrected',
    habitId,
    occurredAt,
    localDate: localDate(new Date(occurredAt)),
    timezoneOffsetMinutes: tzOffsetMinutes(new Date(occurredAt)),
    payload: { ...payload },
  });
}

export interface LogSkippedPurchaseInput {
  habitId: string;
  templateId?: string | undefined;
  label: string;
  currency: string;
  amountAvoided: number;
  episodeId?: string | undefined;
  triggers?: string[] | undefined;
  occurredAt?: string | undefined;
}

/**
 * Append a success_logged event with kind=skipped_purchase. Progress treats
 * this as "money protected"; used by shopping/gambling/digital flows.
 */
export async function logSkippedPurchase(input: LogSkippedPurchaseInput): Promise<AppEvent> {
  const occurredAt = input.occurredAt ?? nowIso();
  return logEvent({
    type: 'success_logged',
    habitId: input.habitId,
    occurredAt,
    localDate: localDate(new Date(occurredAt)),
    timezoneOffsetMinutes: tzOffsetMinutes(new Date(occurredAt)),
    ...(input.episodeId ? { episodeId: input.episodeId } : {}),
    payload: {
      kind: 'skipped_purchase',
      label: input.label,
      currency: input.currency,
      amountAvoided: input.amountAvoided,
      estimatedMoneyProtected: input.amountAvoided,
      ...(input.templateId ? { templateId: input.templateId } : {}),
      ...(input.triggers ? { triggers: input.triggers } : {}),
      ...(input.episodeId ? { episodeId: input.episodeId } : {}),
    },
  });
}

export interface StartCoolingOffInput {
  habitId: string;
  templateId?: string | undefined;
  label: string;
  currency: string;
  amount: number;
  delayHours?: number;
}

export interface CoolingOffPayload {
  coolingOffId: string;
  label: string;
  amount: number;
  currency: string;
  decideAfter: string;
  templateId?: string;
  resolved?: 'bought' | 'skipped' | 'still_deciding';
}

/**
 * Start a cooling-off period for an impulse buy. Writes a
 * purchase_cooling_off event the Now hero surfaces once the delay elapses.
 */
export async function startCoolingOff(input: StartCoolingOffInput): Promise<AppEvent> {
  const id = uuid();
  const delayMs = (input.delayHours ?? 24) * 60 * 60 * 1000;
  const decideAfter = new Date(Date.now() + delayMs).toISOString();
  const occurredAt = nowIso();
  const payload: CoolingOffPayload = {
    coolingOffId: id,
    label: input.label,
    amount: input.amount,
    currency: input.currency,
    decideAfter,
    ...(input.templateId ? { templateId: input.templateId } : {}),
  };
  return logEvent({
    type: 'purchase_cooling_off',
    habitId: input.habitId,
    occurredAt,
    localDate: localDate(new Date(occurredAt)),
    timezoneOffsetMinutes: tzOffsetMinutes(new Date(occurredAt)),
    payload: { ...payload },
  });
}

/**
 * Resolve a cooling-off entry. 'bought' delegates to logPurchase, 'skipped'
 * delegates to logSkippedPurchase. Either way a resolution event is written
 * so the original cooling-off doesn't reappear.
 */
export async function resolveCoolingOff(
  habitId: string,
  pending: CoolingOffPayload,
  outcome: 'bought' | 'skipped' | 'still_deciding',
): Promise<void> {
  const occurredAt = nowIso();
  await logEvent({
    type: 'purchase_cooling_off_resolved',
    habitId,
    occurredAt,
    localDate: localDate(new Date(occurredAt)),
    timezoneOffsetMinutes: tzOffsetMinutes(new Date(occurredAt)),
    payload: {
      coolingOffId: pending.coolingOffId,
      outcome,
      label: pending.label,
      amount: pending.amount,
      currency: pending.currency,
    },
  });
  if (outcome === 'bought') {
    await logPurchase({
      habitId,
      ...(pending.templateId ? { templateId: pending.templateId } : {}),
      label: pending.label,
      currency: pending.currency,
      amount: pending.amount,
      source: 'quick_log',
    });
  } else if (outcome === 'skipped') {
    await logSkippedPurchase({
      habitId,
      ...(pending.templateId ? { templateId: pending.templateId } : {}),
      label: pending.label,
      currency: pending.currency,
      amountAvoided: pending.amount,
    });
  }
}

/**
 * Collect unresolved cooling-off events whose delay has elapsed.
 */
export function pendingCoolingOff(events: AppEvent[], nowMs: number = Date.now()): CoolingOffPayload[] {
  const resolved = new Set<string>();
  for (const e of events) {
    if (e.type !== 'purchase_cooling_off_resolved') continue;
    // Skip undone resolutions so the pending card reappears when the user
    // taps Undo on a resolution toast.
    if (e.status === 'undone') continue;
    const id = (e.payload as { coolingOffId?: string }).coolingOffId;
    if (id) resolved.add(id);
  }
  const pending: CoolingOffPayload[] = [];
  for (const e of events) {
    if (e.type !== 'purchase_cooling_off') continue;
    if (e.status === 'undone') continue;
    const p = e.payload as unknown as CoolingOffPayload;
    if (!p.coolingOffId) continue;
    if (resolved.has(p.coolingOffId)) continue;
    if (new Date(p.decideAfter).getTime() > nowMs) continue;
    pending.push(p);
  }
  return pending;
}

export async function deletePurchase(
  habitId: string,
  payload: PurchaseDeletedPayload,
): Promise<AppEvent> {
  const occurredAt = nowIso();
  return logEvent({
    type: 'purchase_deleted',
    habitId,
    occurredAt,
    localDate: localDate(new Date(occurredAt)),
    timezoneOffsetMinutes: tzOffsetMinutes(new Date(occurredAt)),
    payload: { ...payload },
  });
}
